import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import client from "@/api/client";
import {
  CalendarClock, Users, ClipboardList, Bell, Loader2, Search, ChevronLeft, ChevronRight, Ban, CalendarSync,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { ar as arLocale } from "date-fns/locale";

const FEEDS = [
  { id: "bookings", icon: CalendarClock },
  { id: "rsvps", icon: Users },
  { id: "tasks", icon: ClipboardList },
  { id: "notifications", icon: Bell },
];
const ONLY_BY_FEED = {
  bookings: ["", "pending", "cancel_pending", "reschedule_pending"],
  tasks: ["", "open"],
  notifications: ["", "unread"],
  rsvps: [""],
};
const PAGE = 25;

/** Admin view over member-owned activity: sessions, RSVPs, tasks, alerts. */
export function AdminMemberActivity() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "ar" ? arLocale : undefined;
  const [feed, setFeed] = useState("bookings");
  const [only, setOnly] = useState("");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [skip, setSkip] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);

  const loadCounts = useCallback(async () => {
    try {
      const res = await client.get("/admin/member-activity/counts");
      setCounts(res.data || {});
    } catch { setCounts({}); }
  }, []);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get(
        `/admin/member-activity?feed=${feed}&limit=${PAGE}&skip=${skip}` +
        `${only ? `&only=${only}` : ""}${search ? `&q=${encodeURIComponent(search)}` : ""}`,
      );
      setRows(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [feed, only, search, skip]);
  useEffect(() => { load(); }, [load]);

  const pickFeed = (id) => { setFeed(id); setOnly(""); setSkip(0); };
  const when = (iso) => {
    try { return format(parseISO(iso), "MMM d, yyyy · HH:mm", { locale }); } catch { return iso || "—"; }
  };

  const tiles = [
    { key: "bookings", value: counts.bookings, label: t("memberActivity.feed.bookings", { defaultValue: "Sessions" }) },
    { key: "pending_bookings", value: counts.pending_bookings, label: t("memberActivity.pendingBookings", { defaultValue: "Awaiting mentor" }) },
    { key: "pending_cancellations", value: counts.pending_cancellations, label: t("memberActivity.pendingCancellations", { defaultValue: "Cancellations to review" }), alert: true },
    { key: "pending_reschedules", value: counts.pending_reschedules, label: t("memberActivity.pendingReschedules", { defaultValue: "New times to accept" }), alert: true },
    { key: "rsvps", value: counts.rsvps, label: t("memberActivity.feed.rsvps", { defaultValue: "Event RSVPs" }) },
    { key: "open_tasks", value: counts.open_tasks, label: t("memberActivity.openTasks", { defaultValue: "Open tasks" }) },
    { key: "unread_notifications", value: counts.unread_notifications, label: t("memberActivity.unread", { defaultValue: "Unread alerts" }) },
  ];

  return (
    <div className="space-y-5" data-testid="admin-member-activity">
      <div>
        <h2 className="font-display text-lg font-semibold">
          {t("memberActivity.title", { defaultValue: "Member activity" })}
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          {t("memberActivity.subtitle", { defaultValue: "Everything members do inside the platform — sessions, RSVPs, tasks and the alerts we sent them. Direct messages stay private." })}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2.5" data-testid="member-activity-counts">
        {tiles.map((tile) => (
          <Card
            key={tile.key}
            className={`rounded-xl border p-3 ${tile.alert && tile.value > 0 ? "border-rose-500/40 bg-rose-500/5" : "border-border"}`}
            data-testid={`member-activity-count-${tile.key}`}
          >
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{tile.label}</p>
            <p className={`font-display text-2xl font-semibold mt-0.5 ${tile.alert && tile.value > 0 ? "text-rose-600" : ""}`}>
              {typeof tile.value === "number" ? tile.value : "—"}
            </p>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FEEDS.map((f) => {
          const Icon = f.icon;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => pickFeed(f.id)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-all ${
                feed === f.id ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-primary"
              }`}
              data-testid={`member-activity-feed-${f.id}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t(`memberActivity.feed.${f.id}`, { defaultValue: f.id })}
              <span className="font-mono opacity-80">{counts[f.id] ?? "—"}</span>
            </button>
          );
        })}
        <form
          className="ms-auto flex items-center gap-1.5"
          onSubmit={(e) => { e.preventDefault(); setSkip(0); setSearch(q.trim()); }}
        >
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("memberActivity.search", { defaultValue: "Search titles, notes, status…" })}
            className="rounded-xl h-9 w-[220px]"
            data-testid="member-activity-search"
          />
          <Button type="submit" variant="outline" size="icon" className="rounded-xl h-9 w-9" aria-label="Search" data-testid="member-activity-search-btn">
            <Search className="h-4 w-4" />
          </Button>
        </form>
      </div>

      {(ONLY_BY_FEED[feed] || [""]).length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {(ONLY_BY_FEED[feed] || []).map((o) => (
            <button
              key={o || "all"}
              type="button"
              onClick={() => { setOnly(o); setSkip(0); }}
              className={`rounded-full px-3 py-1 text-[11px] font-medium border transition-all ${
                only === o ? "bg-foreground text-background border-foreground" : "bg-background text-muted-foreground border-border hover:text-foreground"
              }`}
              data-testid={`member-activity-only-${o || "all"}`}
            >
              {t(`memberActivity.only.${o || "all"}`, { defaultValue: o || "All" })}
            </button>
          ))}
        </div>
      )}

      <Card className="rounded-2xl border border-border overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-16 text-center" data-testid="member-activity-empty">
            {t("memberActivity.empty", { defaultValue: "Nothing recorded for this filter." })}
          </p>
        ) : (
          <ul className="divide-y divide-border" data-testid="member-activity-list">
            {rows.map((r, idx) => {
              const id = r.booking_id || r.rsvp_id || r.task_id || r.notification_id || idx;
              const cancelPending = (r.cancel_request || {}).status === "pending";
              const movePending = (r.reschedule_request || {}).status === "pending";
              return (
                <li key={id} className="p-3.5 flex items-start gap-3" data-testid={`member-activity-row-${id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {feed === "bookings" && `${r.owner_name || t("common.member", { defaultValue: "Member" })} → ${r.mentor_name || "—"}`}
                      {feed === "rsvps" && r.event_title}
                      {feed === "tasks" && r.title}
                      {feed === "notifications" && r.content}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-1.5">
                      {feed !== "bookings" && r.owner_name && <span>{r.owner_name}</span>}
                      {feed !== "bookings" && r.owner_name && <span>·</span>}
                      {feed === "bookings" && <span className="font-mono">{when(r.start_time)}</span>}
                      {feed !== "bookings" && <span className="font-mono">{when(r.created_at)}</span>}
                      {r.due_at && <><span>·</span><span className="font-mono">due {r.due_at.slice(0, 10)}</span></>}
                      {r.type && <><span>·</span><span className="font-mono uppercase tracking-widest">{r.type.replace(/_/g, " ")}</span></>}
                    </p>
                    {movePending && (
                      <p className="text-[11px] text-sky-700 mt-1 flex items-center gap-1" data-testid={`member-activity-move-${id}`}>
                        <CalendarSync className="h-3 w-3" />
                        {t("memberActivity.moveRequestedBy", { defaultValue: "New time proposed by" })}{" "}
                        {r.reschedule_request.requested_by_name || "—"} · {when(r.reschedule_request.start_time)}
                      </p>
                    )}
                    {cancelPending && (
                      <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1" data-testid={`member-activity-cancel-${id}`}>
                        <Ban className="h-3 w-3" />
                        {t("memberActivity.cancelRequestedBy", { defaultValue: "Cancellation requested by" })}{" "}
                        {r.cancel_request.requested_by_name || "—"}
                        {r.cancel_request.reason ? ` — “${r.cancel_request.reason}”` : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {r.status && (
                      <Badge variant="outline" className="rounded-full text-[10px] capitalize">
                        {t(`statuses.${r.status}`, { defaultValue: String(r.status).replace(/_/g, " ") })}
                      </Badge>
                    )}
                    {feed === "notifications" && (
                      <Badge variant="outline" className={`rounded-full text-[10px] ${r.is_read ? "" : "border-primary/40 bg-primary/10 text-primary"}`}>
                        {r.is_read ? t("memberActivity.read", { defaultValue: "read" }) : t("memberActivity.unreadOne", { defaultValue: "unread" })}
                      </Badge>
                    )}
                    {cancelPending && (
                      <Badge variant="outline" className="rounded-full text-[10px] border-rose-500/40 bg-rose-500/10 text-rose-600">
                        {t("memberActivity.cancelPending", { defaultValue: "cancel pending" })}
                      </Badge>
                    )}
                    {movePending && (
                      <Badge variant="outline" className="rounded-full text-[10px] border-sky-500/40 bg-sky-500/10 text-sky-700">
                        {t("memberActivity.movePending", { defaultValue: "new time pending" })}
                      </Badge>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground font-mono" data-testid="member-activity-range">
          {total === 0 ? "0" : `${skip + 1}–${Math.min(skip + PAGE, total)}`} / {total}
        </p>
        <div className="flex gap-1.5">
          <Button variant="outline" size="icon" className="rounded-xl h-9 w-9" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))} aria-label="Previous" data-testid="member-activity-prev">
            <ChevronLeft className="h-4 w-4 rtl-flip" />
          </Button>
          <Button variant="outline" size="icon" className="rounded-xl h-9 w-9" disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)} aria-label="Next" data-testid="member-activity-next">
            <ChevronRight className="h-4 w-4 rtl-flip" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AdminMemberActivity;
